import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  Sparkles, ShieldCheck, PlayCircle, FileDown, HeartPulse, Salad, Lock, ArrowRight, CheckCircle2,
  UserPlus, ClipboardList, Unlock, GraduationCap, Award, Quote,
} from "lucide-react";
import { usePageContent } from "../lib/pageContent";

const HERO_DEFAULT = "https://images.unsplash.com/photo-1490645935967-10de6ba17061?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";
const FEATURE_IMG = "https://images.unsplash.com/photo-1685079253777-61c2c0de2956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMHNhbGFkJTIwZmxhdGxheSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc4MzEyMTgzfDA&ixlib=rb-4.1.0&q=85";

const BENEFIT_ICONS = { play: <PlayCircle />, file: <FileDown />, salad: <Salad /> };
const CRED_ICONS = { graduation: <GraduationCap size={18} />, award: <Award size={18} /> };
const STEP_ICONS = [<UserPlus />, <ClipboardList />, <Unlock />];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/uploads/") || url.startsWith("/uploads/"))
    return `${BACKEND}${url.startsWith("/api") ? "" : "/api"}${url}`;
  return url;
};

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-moss/10 text-moss text-xs font-semibold px-3 py-1">
    <Sparkles size={12} /> {children}
  </span>
);

export default function Landing() {
  const c = usePageContent();
  if (!c) return <div className="container-page py-32 text-center text-muted2">Loading…</div>;

  const heroImg = resolveImg(c.hero_image_url) || HERO_DEFAULT;

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-moss/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-terra/10 blur-3xl pointer-events-none" />
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="lg:col-span-7 animate-fade-in-up">
            <Pill>{c.hero_badge}</Pill>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05] text-ink">
              {c.hero_title} <span className="text-moss">{c.hero_highlight}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted2 max-w-xl leading-relaxed">{c.hero_subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register"
                className="inline-flex items-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-7 py-3.5 font-semibold transition-all active:scale-95 shadow-ambient">
                {c.hero_cta_primary} <ArrowRight size={18} />
              </Link>
              <Link to="/login" data-testid="hero-cta-login"
                className="inline-flex items-center gap-2 border-2 border-moss text-moss hover:bg-moss hover:text-white rounded-full px-7 py-3.5 font-semibold transition-all">
                {c.hero_cta_secondary}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted2">
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-leaf" /> Free profile</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-leaf" /> Expert videos</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-leaf" /> PDF library</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-hover border border-subtle">
              <img src={heroImg} alt={c.about_name} className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 flex items-center gap-3 border border-white/60">
                <span className="w-10 h-10 rounded-full bg-moss text-white grid place-items-center"><HeartPulse size={18} /></span>
                <div>
                  <div className="text-sm font-semibold text-ink">{c.hero_image_caption_title}</div>
                  <div className="text-xs text-muted2">{c.hero_image_caption_subtitle}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="container-page py-16 lg:py-24">
        <div className="max-w-2xl">
          <Pill>Why Danashakaa.com</Pill>
          <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold text-ink">{c.benefits_title}</h2>
          <p className="mt-3 text-muted2">{c.benefits_subtitle}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {c.benefits.map((b, i) => (
            <div key={i} className="bg-white border border-subtle rounded-2xl p-7 shadow-ambient hover:shadow-hover hover:-translate-y-1 transition-all" data-testid={`benefit-${i}`}>
              <span className="inline-flex w-12 h-12 rounded-xl bg-moss/10 text-moss items-center justify-center mb-4">{BENEFIT_ICONS[b.icon] || <PlayCircle />}</span>
              <h3 className="font-heading font-semibold text-lg text-ink">{b.title}</h3>
              <p className="text-sm text-muted2 mt-2 leading-relaxed">{b.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT DANA */}
      <section id="about" className="container-page py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Pill>About Dana</Pill>
            <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold text-ink leading-tight">
              Meet <span className="text-moss">{c.about_name}</span>
            </h2>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.2em] text-terra">{c.about_role}</p>
            <div className="mt-6 inline-flex flex-col gap-3 bg-cardm border border-subtle rounded-2xl p-5">
              {c.about_credentials.map((cr, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className={`w-10 h-10 rounded-xl ${cr.icon === "graduation" ? "bg-moss/10 text-moss" : "bg-terra/10 text-terra"} grid place-items-center shrink-0`}>
                    {CRED_ICONS[cr.icon] || <Award size={18} />}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-ink">{cr.title}</p>
                    <p className="text-xs text-muted2">{cr.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 space-y-5">
            {c.about_paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? "text-base md:text-lg text-muted2 leading-relaxed" : "text-muted2 leading-relaxed"} data-testid={`about-bio-${i}`}>{p}</p>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <h3 className="font-heading text-2xl font-semibold text-ink">Areas of expertise</h3>
          <p className="mt-2 text-muted2">Personalised support across a wide range of nutritional and medical needs.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="about-expertise-grid">
            {c.about_expertise.map((item, i) => (
              <div key={i}
                className="flex items-start gap-3 bg-white border border-subtle rounded-2xl px-5 py-4 shadow-ambient hover:shadow-hover hover:border-moss/30 hover:-translate-y-0.5 transition-all"
                data-testid={`expertise-${i}`}>
                <CheckCircle2 size={18} className="text-leaf mt-0.5 shrink-0" />
                <span className="text-sm text-ink leading-snug">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 relative rounded-3xl bg-cardm border border-subtle p-8 lg:p-12 overflow-hidden">
          <Quote size={56} className="absolute top-6 right-6 text-moss/10" />
          <div className="relative max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Dana's philosophy</span>
            <blockquote className="mt-3 font-heading text-2xl sm:text-3xl text-ink leading-snug">"{c.about_philosophy_quote}"</blockquote>
            <p className="mt-6 text-muted2 leading-relaxed">{c.about_philosophy_text}</p>
            <p className="mt-4 text-sm font-semibold text-ink">— {c.about_name}</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-cardm border-y border-subtle">
        <div className="container-page py-16 lg:py-24">
          <div className="max-w-2xl">
            <Pill>How it works</Pill>
            <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold text-ink">{c.how_title}</h2>
            <p className="mt-3 text-muted2">{c.how_subtitle}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.how_steps.map((s, i) => (
              <div key={i} className="bg-white border border-subtle rounded-2xl p-7 shadow-ambient relative" data-testid={`step-${i}`}>
                <span className="absolute top-6 right-6 text-xs font-semibold uppercase tracking-[0.2em] text-soft">{String(i + 1).padStart(2, "0")}</span>
                <span className="inline-flex w-12 h-12 rounded-xl bg-terra/10 text-terra items-center justify-center mb-4">{STEP_ICONS[i] || <Unlock />}</span>
                <h3 className="font-heading font-semibold text-lg text-ink">{s.title}</h3>
                <p className="text-sm text-muted2 mt-2 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <Pill>What you get</Pill>
          <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold text-ink">{c.what_title}</h2>
          <ul className="mt-6 space-y-3 text-muted2">
            {c.what_items.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-leaf mt-0.5 shrink-0" /> <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="relative aspect-video rounded-2xl overflow-hidden border border-subtle bg-white shadow-ambient">
              <img src={FEATURE_IMG} alt="" className="w-full h-full object-cover blur-[2px] scale-105 opacity-80" />
              <div className="absolute inset-0 grid place-items-center">
                <div className="glass rounded-full p-3 border border-white/60"><Lock size={20} className="text-moss" /></div>
              </div>
            </div>
          ))}
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className="bg-white/90 border border-subtle rounded-full px-4 py-1.5 text-xs font-semibold text-moss shadow-ambient">Members-only preview</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-cardm border-y border-subtle">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Pill>FAQ</Pill>
            <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold text-ink">Questions, answered.</h2>
            <p className="mt-3 text-muted2">Can't find what you need? Tap the WhatsApp button to talk to us.</p>
          </div>
          <div className="lg:col-span-8" data-testid="faq-section">
            <Accordion type="single" collapsible className="bg-white border border-subtle rounded-2xl divide-y divide-subtle">
              {c.faq_items.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="px-5 border-0">
                  <AccordionTrigger className="font-heading font-medium text-ink py-5" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted2 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-page py-12">
        <div className="rounded-3xl bg-moss text-white p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center shadow-hover">
          <div>
            <h2 className="text-3xl lg:text-4xl font-heading font-semibold leading-tight">Ready to feel better in your body?</h2>
            <p className="mt-3 text-white/80">Create your free account in 30 seconds. No credit card required.</p>
          </div>
          <div className="lg:text-right">
            <Link to="/register" data-testid="bottom-cta-register"
              className="inline-flex items-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-7 py-3.5 font-semibold transition-all active:scale-95">
              Start free <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
